import { LESSON_PASS_THRESHOLD } from "../courses/pass-threshold";
import type { BlockRegistry } from "./registry";
import type {
  AuthoredBlock,
  BlockResponse,
  GradeResult,
  LessonResult,
  ValidationResult,
} from "./types";

/**
 * Evaluation engine. Unaware of concrete types: for each block it looks up
 * plugin in registry and delegates. Adding a new type does not touch this file.
 */

const UNKNOWN_TYPE_RESULT = (type: string, weight: number): GradeResult => ({
  correct: false,
  score: 0,
  maxScore: weight,
  feedback: `Tipo de bloque desconocido: "${type}"`,
});

function weightOf(block: AuthoredBlock): number {
  return block.weight != null && block.weight > 0 ? block.weight : 1;
}

/** Validates block authoring definition (for saving in editor). */
export function validateBlock(
  registry: BlockRegistry,
  block: AuthoredBlock,
): ValidationResult<unknown> {
  const plugin = registry.get(block.type);
  if (!plugin) {
    return { ok: false, errors: [`Tipo desconocido: "${block.type}"`] };
  }
  return plugin.validate(block.def);
}

/** Grades a single block. Deterministic; reproducible on server and client. */
export function gradeBlock(
  registry: BlockRegistry,
  block: AuthoredBlock,
  response: unknown,
): GradeResult {
  const weight = weightOf(block);
  const plugin = registry.get(block.type);
  if (!plugin) {
    return UNKNOWN_TYPE_RESULT(block.type, weight);
  }
  const validated = plugin.validate(block.def);
  if (!validated.ok) {
    return {
      correct: false,
      score: 0,
      maxScore: weight,
      feedback: `Definición inválida: ${validated.errors.join("; ")}`,
    };
  }
  const result = plugin.grade(validated.def, response);
  // Block weight takes precedence over plugin-declared maxScore.
  return { ...result, maxScore: weight };
}

export interface GradeLessonOpts {
  /** 0..1 threshold to consider lesson passed (default LESSON_PASS_THRESHOLD). */
  passThreshold?: number;
}

/**
 * Grades an entire lesson (block list) against student answers.
 * Score weighted by `weight`. `info` blocks (effective weight 0)
 * or non-graded blocks can be excluded via weight.
 */
export function gradeLesson(
  registry: BlockRegistry,
  blocks: readonly AuthoredBlock[],
  responses: readonly BlockResponse[],
  opts: GradeLessonOpts = {},
): LessonResult {
  const byId = new Map<string, unknown>();
  for (const r of responses) {
    byId.set(r.blockId, r.response);
  }
  const perBlock = new Map<string, GradeResult>();
  let earned = 0;
  let total = 0;
  for (const block of blocks) {
    const result = gradeBlock(registry, block, byId.get(block.id));
    perBlock.set(block.id, result);
    earned += result.score * result.maxScore;
    total += result.maxScore;
  }
  const score = total === 0 ? 0 : earned / total;
  const threshold = opts.passThreshold ?? LESSON_PASS_THRESHOLD;
  return { score, earned, total, passed: total > 0 && score >= threshold, perBlock };
}
